import { TouchEvent, useEffect, useRef } from 'react';

import { useContextMediaQuery } from '@core/context';
import { useCarouselSteps } from '@core/hook';
import { EMedia, MediaEntity } from '@core/type/api';
import { SanityMedia } from '@core/type/sanity';
import { chunkArray } from '@core/util';

import { SCROLL_EXTRA_DELTA_X, useCarouselSwipe } from '../../banner/use-carouse-swipe';

export const COUNT_OF_MEDIA_PER_SLIDE = 5;

export const useCarouselProducts = (medias: SanityMedia[] | MediaEntity[]) => {
  const startTouchXRef = useRef<number>(null);
  const endTouchXRef = useRef<number>(null);
  const startTouchYRef = useRef<number>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentStepRef = useRef<number>(0);

  const { isMobile, mediaQuery } = useContextMediaQuery();

  const {
    currentStep,
    onPrevious,
    onNext,
    onClick: setSlide,
    ...restCarouselProps
  } = useCarouselSteps();

  useCarouselSwipe(startTouchXRef, endTouchXRef, startTouchYRef);

  // Need to hide the video iframe on mobile as it's not supported by the carousel
  const formattedMedias = isMobile
    ? medias.filter((media) =>
        '_type' in media ? media._type === 'image' : media.mediaType === EMedia.IMAGE,
      )
    : chunkArray<MediaEntity | SanityMedia>(medias, COUNT_OF_MEDIA_PER_SLIDE);

  currentStepRef.current = currentStep;
  const totalSteps = formattedMedias?.length || 0;

  const getTotalSlidesWidthBeforeCurrent = () => {
    const slideWidth = containerRef?.current?.offsetWidth;

    return currentStep * slideWidth;
  };

  useEffect(() => {
    if (carouselRef?.current?.style && containerRef?.current) {
      carouselRef.current.style.transform = `translateX(-${getTotalSlidesWidthBeforeCurrent()}px)`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, containerRef?.current?.offsetWidth]);

  useEffect(() => {
    setSlide(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaQuery]);

  const handleTouchStart = ({ touches }: TouchEvent) => {
    if (totalSteps > 1) {
      startTouchXRef.current = touches[0]?.clientX;
      startTouchYRef.current = touches[0]?.clientY;
    }
  };

  const handleTouchMove = ({ touches }: TouchEvent) => {
    endTouchXRef.current = touches[0]?.clientX;

    if (startTouchXRef.current === null || carouselRef.current === null) {
      return;
    }

    const deltaX = touches[0]?.clientX - startTouchXRef.current;
    const deltaY = touches[0]?.clientY - startTouchYRef.current;

    if (Math.abs(deltaX) + SCROLL_EXTRA_DELTA_X > Math.abs(deltaY)) {
      carouselRef.current.style.transform = `translateX(calc(-${getTotalSlidesWidthBeforeCurrent()}px + ${deltaX}px))`;
    } else {
      startTouchXRef.current = null;
      startTouchYRef.current = null;
      carouselRef.current.style.transform = `translateX(-${getTotalSlidesWidthBeforeCurrent()}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (startTouchXRef.current === null || endTouchXRef.current === null) {
      return;
    }

    const deltaX = startTouchXRef.current - endTouchXRef.current;

    if (deltaX > 0) {
      handleNext();
    } else if (deltaX < 0) {
      handlePrevious();
    }

    if (currentStep === 0) {
      carouselRef.current.style.transform = 'translateX(0px)';
    } else if (currentStep === totalSteps - 1) {
      carouselRef.current.style.transform = `translateX(-${getTotalSlidesWidthBeforeCurrent()}px)`;
    }

    endTouchXRef.current = null;
    startTouchXRef.current = null;
    startTouchYRef.current = null;
  };

  const handlePrevious = () => {
    if (currentStep === 0) {
      return setSlide(totalSteps - 1);
    }

    onPrevious();
  };

  const handleNext = () => {
    if (currentStep === totalSteps - 1) {
      return setSlide(0);
    }

    onNext();
  };

  return {
    ref: carouselRef,
    containerRef,
    currentStep,
    slides: formattedMedias,
    totalSteps,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    handlePrevious,
    handleNext,
    setSlide,
    ...restCarouselProps,
  };
};
